import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useReactFlow } from "reactflow";
import "reactflow/dist/style.css";
import { cn } from "@/lib/utils";
import { useLineCanvas, useUpdateCanvas, useDebouncedViewportSave } from "../hooks";
import { isDraftLineId } from "../draft-canvas-adapter";
import { useLineMachines, type MachineDetail } from "@/features/lines/hooks";
import { NodePropertiesPanel } from "../NodePropertiesPanel";
import { CanvasEditToolbar } from "./CanvasEditToolbar";
import { CanvasReactFlowSurface } from "./CanvasReactFlowSurface";
import { useCanvasDraft } from "./hooks/useCanvasDraft";
import { useCanvasMutations } from "./hooks/useCanvasMutations";
import { useCanvasFlowEvents } from "./hooks/useCanvasFlowEvents";
import { useCanvasFocusEvents } from "./hooks/useCanvasFocusEvents";
import { useDraftCanvasMirror } from "./hooks/useDraftCanvasMirror";
import { ValidationBanners } from "./ValidationBanners";
import { CanvasStatePlaceholder } from "./CanvasStatePlaceholder";
import type { ProductionLineCanvasProps } from "./index";

export function CanvasInner({
  lineId,
  mode = "display",
  onMachineClick,
  height = "100%",
  compact = false,
  hideMiniMap = false,
  isRunning = false,
  onSaved,
  onCancel,
  onOpenSidebar,
  pendingMachineAssignment,
  onAssignmentApplied,
  saveLabel,
  onSaveOverride,
  onBeforeSave,
  externalCanSave,
  onCreateNewMachine,
  pendingMachineAdd,
  onMachineAddApplied,
  onEditCanvasChange,
}: ProductionLineCanvasProps) {
  const { data: canvas, isLoading, error } = useLineCanvas(lineId);
  const { data: machines = [] } = useLineMachines(lineId, 30_000);
  const update = useUpdateCanvas(lineId);
  const scheduleViewportSave = useDebouncedViewportSave(lineId);
  const flow = useReactFlow();

  const isEdit = mode === "edit";
  const [machinePickerOpen, setMachinePickerOpen] = useState(false);

  const machineById = useMemo(() => {
    const m = new Map<string, MachineDetail>();
    for (const machine of machines) m.set(machine.id, machine);
    return m;
  }, [machines]);

  const draft = useCanvasDraft({
    canvas,
    isEdit,
    machineById,
    pendingMachineAssignment,
    onAssignmentApplied,
  });

  const mutations = useCanvasMutations({
    isEdit,
    isRunning,
    isDirty: draft.isDirty,
    draftNodes: draft.draftNodes,
    draftEdges: draft.draftEdges,
    selectedNodeId: draft.selectedNodeId,
    draftCanvasNodes: draft.draftCanvasNodes,
    draftCanvasEdges: draft.draftCanvasEdges,
    validation: draft.validation,
    setDraftNodes: draft.setDraftNodes,
    setDraftEdges: draft.setDraftEdges,
    setIsDirty: draft.setIsDirty,
    setSelectedNodeId: draft.setSelectedNodeId,
    announce: draft.announce,
    flow,
    update,
    onMachineClick,
    onSaved,
    onCancel,
    machineById,
    onSaveOverride,
    onBeforeSave,
  });

  useEffect(() => {
    if (!pendingMachineAdd || !isEdit) return;
    mutations.handleAddMachineFromPicker(pendingMachineAdd);
    onMachineAddApplied?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMachineAdd, isEdit]);

  useCanvasFocusEvents({ flow, setSelectedNodeId: draft.setSelectedNodeId });

  useDraftCanvasMirror({
    isDraft: isDraftLineId(lineId),
    draftCanvasNodes: draft.draftCanvasNodes,
    draftCanvasEdges: draft.draftCanvasEdges,
    // De onde o rascunho é semeado: a trava do mirror precisa saber se a semeadura já aconteceu.
    serverCanvas: canvas,
    onEditCanvasChange: isEdit ? onEditCanvasChange : undefined,
  });

  const flowEvents = useCanvasFlowEvents({
    isEdit,
    draftNodes: draft.draftNodes,
    selectedNodeId: draft.selectedNodeId,
    setDraftNodes: draft.setDraftNodes,
    setDraftEdges: draft.setDraftEdges,
    setIsDirty: draft.setIsDirty,
    setSelectedNodeId: draft.setSelectedNodeId,
    announce: draft.announce,
    handleNodeDelete: mutations.handleNodeDelete,
  });

  if (isLoading) return <CanvasStatePlaceholder height={height} variant="loading" />;
  if (error) return <CanvasStatePlaceholder height={height} variant="error" error={error} />;

  if (!canvas) return null;

  const usedMachineIds = new Set<string>(
    draft.draftCanvasNodes
      .filter((n) => n.type === "MACHINE" && typeof n.data?.machineId === "string")
      .map((n) => n.data.machineId as string)
  );
  const selectedDraft =
    isEdit && draft.selectedNodeId
      ? (draft.draftCanvasNodes.find((n) => n.id === draft.selectedNodeId) ?? null)
      : null;

  const showEmpty = !isEdit && canvas.nodes.length === 0;

  return (
    <div className={cn("flex flex-col gap-2", height === "100%" && "h-full")}>
      {}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {draft.liveMessage}
      </div>
      {isEdit ? (
        <CanvasEditToolbar
          machinePickerOpen={machinePickerOpen}
          setMachinePickerOpen={setMachinePickerOpen}
          machines={machines}
          usedMachineIds={usedMachineIds}
          onAddNode={mutations.handleAddNode}
          onAddMachineFromPicker={mutations.handleAddMachineFromPicker}
          onSave={() => void mutations.handleSave()}
          onCancel={() => void mutations.handleCancel()}
          canSave={externalCanSave ?? draft.validation.canSave}
          isDirty={draft.isDirty}
          isSaving={update.isPending}
          saveLabel={saveLabel}
          onCreateNewMachine={onCreateNewMachine}
        />
      ) : null}

      {isEdit ? (
        <ValidationBanners errors={draft.validation.errors} warnings={draft.validation.warnings} />
      ) : null}

      <div
        className="relative h-[var(--canvas-h)] w-full rounded-md border bg-card"
        style={
          { "--canvas-h": typeof height === "number" ? `${height}px` : height } as CSSProperties
        }
        role="application"
        aria-label={
          isEdit
            ? "Canvas do fluxo em edição. Clique em um nó para selecionar. Setas movem 10px (Shift+Setas movem 50px). Delete remove. Esc limpa seleção."
            : "Canvas do fluxo da linha de produção"
        }
      >
        {showEmpty ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Sem fluxo configurado para esta linha.
          </div>
        ) : (
          <CanvasReactFlowSurface
            canvas={canvas}
            rfNodes={draft.rfNodes}
            rfEdges={draft.rfEdges}
            draftNodes={draft.draftNodes}
            isEdit={isEdit}
            compact={compact}
            hideMiniMap={hideMiniMap}
            flowEvents={flowEvents}
            handleNodeClick={mutations.handleNodeClick}
            scheduleViewportSave={scheduleViewportSave}
          />
        )}
      </div>

      {selectedDraft ? (
        <NodePropertiesPanel
          node={selectedDraft}
          lineId={lineId}
          onChange={mutations.handleNodeChange}
          onDelete={mutations.handleNodeDelete}
          onClose={() => draft.setSelectedNodeId(null)}
          onOpenSidebar={onOpenSidebar}
          usedMachineIds={usedMachineIds}
        />
      ) : null}
    </div>
  );
}
